import React from "react";
import { MODS } from "../../../constants/modules";
import { TYPES } from "../../../constants/wineTypes";
import { DropdownMenu } from "../../../components/ui/DropdownMenu";
import type { DropdownMenuItem } from "../../../components/ui/DropdownMenu";

interface ChatInputPanelProps {
  canGen: boolean;
  loading: boolean;
  mode: "text" | "pdf" | "camera";
  pdfName: string;
  pour: number;
  tab: string;
  wine: string;
  wineType: string | null;
  onGenerate: () => void;
  onOpenCamera: () => void;
  onOpenCsv: () => void;
  onOpenPdf: () => void;
  onResetMode: () => void;
  onTabChange: (tab: string) => void;
  onWineChange: (wine: string) => void;
  onWineTypeChange: (wineType: string | null) => void;
}

export function ChatInputPanel({
  canGen,
  loading,
  mode,
  pdfName,
  pour,
  tab,
  wine,
  wineType,
  onGenerate,
  onOpenCamera,
  onOpenCsv,
  onOpenPdf,
  onResetMode,
  onTabChange,
  onWineChange,
  onWineTypeChange,
}: ChatInputPanelProps) {
  const mod = MODS.find((item) => item.id === tab);
  const selectedWineType = TYPES.find((item) => item.id === wineType);

  return (
    <div className="space-y-5 animate-[fadeUp_.6s_cubic-bezier(.16,1,.3,1)]">
      <div className="grid gap-3 md:grid-cols-3">
        {MODS.map((item) => {
          const active = item.id === tab;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onTabChange(item.id)}
              className={`rounded-[22px] border p-5 text-left transition-all ${active ? "border-accent-border bg-surface shadow-[0_18px_40px_rgba(217,102,60,0.08)]" : "border-border bg-surface/70 hover:-translate-y-1 hover:shadow-[0_18px_30px_rgba(58,20,32,0.08)]"}`}
            >
              <div className="flex items-center justify-between">
                <span className={`flex h-9 w-9 items-center justify-center rounded-[12px] font-display text-[14px] font-extrabold ${active ? "bg-accent text-[#FFF6EE]" : "bg-accent/10 text-accent"}`}>
                  {item.n}
                </span>
                {active && <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-[12px] font-bold text-[#FFF6EE]">✓</span>}
              </div>
              <div className="mt-4 font-display text-[20px] font-bold tracking-[-0.04em] text-foreground">{item.label}</div>
              <div className="mt-1 text-[13px] leading-5 text-tertiary">{item.sub}</div>
            </button>
          );
        })}
      </div>

      <div className={`relative rounded-[28px] border bg-surface p-6 shadow-[0_20px_50px_rgba(58,20,32,0.06)] transition-colors duration-200 ${canGen ? "border-accent-border" : "border-border"}`}>
      {pour > 0 && (
        <div
          className="absolute left-0 top-0 z-[2] h-1 rounded-t-[28px] bg-accent transition-[width] duration-100"
          style={{ width: `${pour}%` }}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="text-[12px] font-bold uppercase tracking-[0.2em] text-tertiary">Le vin</label>
        <div className="flex gap-1 rounded-full bg-background/70 p-1">
          {[{ id: "text", label: "Texte" }, { id: "pdf", label: "PDF" }, { id: "camera", label: "Photo" }].map((item) => {
            const active = mode === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  if (item.id === "pdf") onOpenPdf();
                  if (item.id === "camera") onOpenCamera();
                  if (item.id === "text") onResetMode();
                }}
                className={`min-h-[36px] rounded-full px-3 text-[12px] font-bold transition-all ${active ? "bg-accent text-[#FFF6EE]" : "text-tertiary"}`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          {mode === "text" ? (
            <input
              value={wine}
              onChange={(event) => onWineChange(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && onGenerate()}
              placeholder="Gevrey-Chambertin 1er Cru Lavaux 2019…"
              className="w-full rounded-[18px] border border-border bg-background px-4 py-4 text-[16px] text-foreground outline-none transition-colors focus:border-accent-border focus:ring-4 focus:ring-accent/10"
            />
          ) : (
            <div className="rounded-[18px] border border-border bg-background px-4 py-4 text-[15px] font-medium text-foreground">
              {mode === "pdf" ? `PDF · ${pdfName}` : "Photo · étiquette"}
            </div>
          )}
          {mode === "text" && !wine && (
            <div className="mt-2 text-[13px] text-tertiary">
              ou glissez un PDF · photographiez l'étiquette · importez un CSV
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <DropdownMenu
            trigger={
              <button
                aria-label="Plus d'options"
                className="flex h-11 items-center justify-center rounded-[14px] border border-border bg-transparent px-4 text-[14px] font-semibold text-secondary transition-all data-[state=open]:border-accent-border data-[state=open]:bg-accent-bg data-[state=open]:text-accent"
              >
                Outils
              </button>
            }
            items={[
              {
                value: "camera",
                label: "Photo",
                icon: <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="1" y="3" width="11" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.1" /><circle cx="6.5" cy="7" r="2" stroke="currentColor" strokeWidth="1" /><path d="M5 3l.5-1h2l.5 1" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" /></svg>,
                onSelect: onOpenCamera,
              },
              {
                value: "pdf",
                label: "PDF",
                icon: <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="2.5" y="1" width="8" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.1" /><path d="M4.5 5h4M4.5 7.5h4M4.5 10h2.5" stroke="currentColor" strokeWidth="0.85" strokeLinecap="round" /></svg>,
                onSelect: onOpenPdf,
              },
              {
                value: "csv",
                label: "Importer CSV",
                icon: <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="1.5" y="1.5" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1" /><path d="M4.5 4.5h4M4.5 6.5h4M4.5 8.5h2" stroke="currentColor" strokeWidth="0.85" strokeLinecap="round" /><circle cx="10" cy="10" r="3" className="fill-background" stroke="currentColor" strokeWidth="0.9" /><path d="M10 8.5v3M8.5 10h3" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" /></svg>,
                onSelect: onOpenCsv,
              },
              ...(mode !== "text"
                ? [{
                    value: "back",
                    label: "Retour texte",
                    icon: <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M8 2L3 6.5L8 11" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" /></svg>,
                    onSelect: onResetMode,
                  } as DropdownMenuItem]
                : []),
            ]}
          />
          {mode === "text" && (
            <DropdownMenu
              selectedValue={wineType ?? undefined}
              trigger={
                <button
                  aria-label="Sélectionner le type de vin"
                  className="flex min-h-[44px] items-center gap-2 rounded-[14px] border px-4 text-[13px] font-semibold transition-all duration-200 data-[state=open]:border-accent-border data-[state=open]:text-accent"
                  style={{
                    background: selectedWineType ? `${selectedWineType.color}18` : "rgb(var(--color-foreground) / 0.03)",
                    borderColor: selectedWineType?.color,
                    color: selectedWineType?.color ?? "rgb(var(--color-secondary))",
                  }}
                >
                  {selectedWineType ? (
                    <>
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: selectedWineType.color }} />
                      {selectedWineType.short}
                    </>
                  ) : (
                    <>
                      <span className="h-2 w-2 rounded-full shrink-0 bg-foreground/20" />
                      Type de vin
                    </>
                  )}
                </button>
              }
              items={[
                ...(wineType
                  ? [{
                      value: "__clear__",
                      label: "Aucun type",
                      icon: <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M2 2l7 7M9 2l-7 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>,
                      onSelect: () => onWineTypeChange(null),
                    } as DropdownMenuItem]
                  : []),
                ...TYPES.map((item) => ({
                  value: item.id,
                  label: item.short,
                  color: item.color,
                  onSelect: () => onWineTypeChange(wineType === item.id ? null : item.id),
                })),
              ]}
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onGenerate}
            disabled={loading || !canGen}
            className={`relative min-h-[48px] min-w-[170px] overflow-hidden rounded-[16px] px-5 text-[15px] font-bold transition-all duration-200 ${!canGen ? "cursor-not-allowed border border-foreground/[0.05] bg-transparent text-foreground/[0.15]" : loading ? "cursor-not-allowed border border-accent/20 bg-accent/10 text-accent/50" : "cursor-pointer bg-accent text-[#FFF6EE] shadow-[0_10px_24px_rgba(217,102,60,0.28)] hover:-translate-y-0.5"}`}
          >
            {pour > 0 && (
              <div
                className="absolute inset-0 rounded-[16px]"
                style={{ background: `linear-gradient(90deg,rgba(122,36,56,0.28) ${pour}%,transparent ${pour}%)` }}
              />
            )}
            <span className="relative z-[1] inline-flex items-center gap-2">
              {loading ? "Génération…" : canGen ? `Générer ${mod?.label ?? ""}` : "Générer"}
            </span>
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}